/**
 * siteRoutes — les adresses du site desktop, et leur traduction dans les deux
 * sens avec les liens imprimés (07/09/2026, SITE_WEB_DESKTOP.md §2 D6 et §5 Q1).
 *
 * Deux vérités cohabitent et doivent rester convertibles :
 *
 *   · le CONTRAT PUBLIC de l'app — `?page=<slug>`, `?monument=`, `?personnage=`,
 *     `?tresor=`, `?evenement=`, `?lang=`, `?couche=` — imprimé sur des QR, des
 *     cartes de lit et des liens déjà envoyés (lib/monumentShare.js : « Rename
 *     one and those links go dark »). Sur un téléphone il reste LA forme.
 *   · les CHEMINS du site — `/autun/monuments/porte-arroux`, préfixés de la
 *     langue pour les six langues non françaises (`/en/…`) parce qu'une page
 *     anglaise indexable a besoin de sa propre adresse (hreflang). Le
 *     français reste nu : c'est la langue de la maison.
 *
 * `legacyToSite` lit la première pour produire la seconde (un desktop qui
 * arrive par un QR atterrit sur la page du site) ; `siteToLegacy` fait le
 * chemin inverse (un téléphone qui reçoit un lien du site ouvre l'app sur la
 * même fiche). Pur, sans DOM, testé dans siteRoutes.test.js.
 */
import { LANGUAGES } from './i18n';
import { FICHE_PARAMS } from './monumentShare';
import { FRISE_COUCHES } from './frise';

export const SITE_LANGS = Object.freeze(LANGUAGES.map((l) => l.code));
export const SITE_DEFAULT_LANG = 'fr';

/**
 * Les pages du site, par nom. Le motif est le chemin FR sans préfixe ;
 * `:id` / `:couche` sont les paramètres. L'ordre n'a pas d'importance.
 */
export const SITE_PAGES = Object.freeze({
    home: '',
    monuments: 'autun/monuments',
    monument: 'autun/monuments/:id',
    tresors: 'autun/tresors',
    tresor: 'autun/tresors/:id',
    figures: 'autun/figures',
    figure: 'autun/figures/:id',
    fetes: 'autun/fetes',
    fete: 'autun/fetes/:id',
    autun: 'autun',
    frise: 'frise',
    friseCouche: 'frise/:couche',
    balades: 'balades',
    balade: 'balades/:id',
    manoir: 'manoir',
    suite: 'manoir/:id',
    bibliotheque: 'bibliotheque',
    conte: 'bibliotheque/:id',
    medard: 'medard',
    mentionsLegales: 'mentions-legales',
    livre: 'livre',
    // audit 10/09 bis — la carte d'Autun : tous les monuments à point, sur le
    // canevas du téléphone (parcours n°6, « chantier futur » de la 1ʳᵉ vague).
    carte: 'carte',
    // vague du 14/09/2026 — les six films du canon, réunis. Ils vivaient
    // chacun sur sa fiche : six films derrière 221 cartes.
    films: 'films',
    // 10/10/2026 — « La cité antique en 3D » : le modèle Blender
    // d'Augustodunum, que le téléphone ouvrait seul (#1057). Le plan 3D
    // (AUGUSTODUNUM_3D_VR.md, Étage 5) en fait l'OUTIL DE RELECTURE qu'un
    // archéologue ouvre depuis un lien, sur un ordinateur : le site lui doit
    // donc une adresse.
    citeAntique: 'cite-antique',
});

/**
 * Le slug que le TÉLÉPHONE lit pour ouvrir la cité en 3D : `?page=cite-antique`.
 * Ce n'est ni un chapitre du Livre ni une sous-page d'onglet — le visualiseur
 * est un calque du Menu (MenuSheet) —, donc MedardExperience le lit à part, une
 * fois, au montage. Écrit ICI parce que les deux sens de la traduction
 * (`legacyToSite`, `siteToLegacy`) et l'app doivent dire le même mot.
 */
export const CITE_PAGE = 'cite-antique';

/**
 * Le paramètre qui nomme le monument vers lequel la cité en 3D VOLE — sur le
 * site (`/cite-antique?monument=<id>`) comme sur le téléphone
 * (`?page=cite-antique&monument=<id>`). C'est le MÊME mot que le contrat des
 * fiches partagées (FICHE_PARAMS.monument), exprès : un même id de canon, lu
 * de la même façon. Mais sur la page de la cité il appartient à LA PAGE — ce
 * n'est jamais un lien de fiche à traduire (SITE_PAGE_QUERY, ci-dessous).
 */
export const CITE_FOCUS_PARAM = FICHE_PARAMS.monument;

/**
 * La requête qu'une page du site POSSÈDE (10/10/2026). Sur ces adresses, le
 * paramètre règle la page elle-même :
 *   · SiteApp ne le traduit pas comme un lien imprimé — sans quoi
 *     `/cite-antique?monument=theatre-romain` partait sur la FICHE du théâtre ;
 *   · le serveur ne lui sert pas la carte sociale d'une fiche partagée, et la
 *     tête reste celle de la page (backend `site_seo.SITE_PAGE_QUERY`, son
 *     MIROIR, tenu par test_site_seo.py — d'où l'écriture littérale) ;
 *   · il n'entre jamais dans la canonique : la page est UNE page, quel que
 *     soit le monument sur lequel elle s'ouvre (lib/siteHead `adresseNue`).
 * La langue, elle, ne passe jamais par ici : elle vit dans le chemin.
 */
export const SITE_PAGE_QUERY = Object.freeze({
    citeAntique: Object.freeze(['monument']),
});

function asParams(source) {
    if (source instanceof URLSearchParams) return source;
    if (source && typeof source === 'object') {
        const p = new URLSearchParams();
        Object.entries(source).forEach(([k, v]) => {
            if (v !== null && v !== undefined && v !== '') p.set(k, String(v));
        });
        return p;
    }
    try {
        return new URLSearchParams(typeof source === 'string' ? source : '');
    } catch {
        return new URLSearchParams();
    }
}

/**
 * Les seuls paramètres que la page `name` possède, tirés d'une requête (une
 * chaîne `?a=b` ou un objet) : '' ou '?monument=theatre-romain'. Une page qui
 * ne possède rien rend toujours ''.
 */
export function pageQuery(name, source) {
    const own = SITE_PAGE_QUERY[name];
    if (!own) return '';
    const from = asParams(source);
    const out = new URLSearchParams();
    own.forEach((k) => {
        const v = (from.get(k) || '').trim();
        if (v) out.set(k, v);
    });
    const q = out.toString();
    return q ? `?${q}` : '';
}

/** La requête PRIVÉE des paramètres que la page possède — ce qui reste à
 *  lire comme une adresse de l'app (`legacyToSite`). */
export function legacyQuery(name, search) {
    const params = asParams(search);
    (SITE_PAGE_QUERY[name] || []).forEach((k) => params.delete(k));
    const q = params.toString();
    return q ? `?${q}` : '';
}

/**
 * Le lancement du téléphone par l'adresse de la cité : `?page=cite-antique`,
 * et le monument qu'elle vise (`&monument=<id>`). null quand l'adresse n'est
 * pas celle de la cité. Lu UNE fois par MedardExperience.
 */
export function readCiteLaunch(search) {
    const params = asParams(search);
    if (params.get('page') !== CITE_PAGE) return null;
    const monument = (params.get(CITE_FOCUS_PARAM) || '').trim();
    return { monument: monument || null };
}

/**
 * Les pages que le code REND aujourd'hui. Une page déclarée mais non
 * implémentée n'apparaît dans aucune barre, aucun pied, aucune carte : la
 * maison n'affiche jamais une porte qui ne mène nulle part (Loi 1). La liste
 * grandit vague après vague ; SiteHeader / SiteFooter la lisent.
 */
export const SITE_IMPLEMENTED = Object.freeze(new Set([
    'home', 'livre',
    'monuments', 'monument',
    // vague 2b (07/09/2026)
    'tresors', 'tresor', 'figures', 'figure', 'fetes', 'fete',
    // vague 3a (07/09/2026) — la frise des époques
    'frise', 'friseCouche',
    // vague 3c (07/09/2026) — les balades de Médard
    'balades', 'balade',
    // vague 3d (07/09/2026) — le carrefour du canon, qui permet à la barre
    // de passer de huit entrées à cinq sans rien cacher
    'autun',
    // vague 3e (07/09/2026) — La Bibliothèque de Médard
    'bibliotheque', 'conte',
    // vague 3f (08/09/2026) — la maison et ses huit suites
    'manoir', 'suite',
    // vague 3g (08/09/2026) — la DERNIÈRE porte : le gardien et ses lois
    'medard',
    // vague 3k (08/09/2026) — l'identité de la maison, telle qu'elle est déclarée
    'mentionsLegales',
    // audit 10/09 bis (11/09/2026) — la carte d'Autun
    'carte',
    // vague du 14/09/2026 — la galerie des films
    'films',
    // 10/10/2026 — la cité antique en 3D
    'citeAntique',
]));

export const isImplemented = (name) => SITE_IMPLEMENTED.has(name);

/**
 * Toutes les pages du site qu'un lien IMPRIMÉ peut désigner — les
 * destinations de `?page=` et celles des quatre paramètres de fiche.
 *
 * Exportée pour être GARDÉE : tant qu'une seule n'est pas construite, lever
 * la garde ferait tomber un QR sur la 404 de Médard (siteRoutes.test.js).
 */
export function legacyDestinations() {
    return { ...PAGE_TO_SITE, ...FICHE_TO_SITE };
}

const PATTERNS = Object.entries(SITE_PAGES).map(([name, pattern]) => ({
    name,
    segments: pattern ? pattern.split('/') : [],
}));

function cleanSegments(pathname) {
    return String(pathname || '')
        .split('?')[0]
        .split('#')[0]
        .split('/')
        .filter(Boolean)
        .map((s) => {
            try { return decodeURIComponent(s); } catch { return s; }
        });
}

/** '/en/autun' → { lang: 'en', segments: ['autun'] } ; '/' → { lang: 'fr', segments: [] } */
export function splitLang(pathname) {
    const segments = cleanSegments(pathname);
    if (segments.length && segments[0] !== SITE_DEFAULT_LANG && SITE_LANGS.includes(segments[0])) {
        return { lang: segments[0], segments: segments.slice(1) };
    }
    // Un `/fr/...` explicite est accepté et normalisé vers le chemin nu.
    if (segments[0] === SITE_DEFAULT_LANG) return { lang: 'fr', segments: segments.slice(1) };
    return { lang: SITE_DEFAULT_LANG, segments };
}

/**
 * Lit un chemin du site. Retourne { lang, name, params } — `name` vaut null
 * quand le chemin ne correspond à aucune page (une 404 du site, ou une autre
 * route de l'app comme /admin, que l'appelant a déjà écartée).
 */
export function parseSitePath(pathname) {
    const { lang, segments } = splitLang(pathname);
    for (const { name, segments: pattern } of PATTERNS) {
        if (pattern.length !== segments.length) continue;
        const params = {};
        let ok = true;
        for (let i = 0; i < pattern.length; i += 1) {
            if (pattern[i].startsWith(':')) {
                if (!segments[i]) { ok = false; break; }
                params[pattern[i].slice(1)] = segments[i];
            } else if (pattern[i] !== segments[i]) {
                ok = false;
                break;
            }
        }
        if (ok) return { lang, name, params };
    }
    return { lang, name: null, params: {} };
}

/** Le chemin d'une page dans une langue : sitePath('monument', {id}, 'en') → '/en/autun/monuments/<id>' */
export function sitePath(name, params = {}, lang = SITE_DEFAULT_LANG) {
    const pattern = SITE_PAGES[name];
    if (pattern === undefined) return null;
    const segments = pattern
        ? pattern.split('/').map((s) => {
            if (!s.startsWith(':')) return s;
            const value = params[s.slice(1)];
            return value == null ? null : encodeURIComponent(String(value));
        })
        : [];
    if (segments.some((s) => s === null)) return null;
    const prefix = lang && lang !== SITE_DEFAULT_LANG && SITE_LANGS.includes(lang) ? [lang] : [];
    const all = [...prefix, ...segments];
    return `/${all.join('/')}`;
}

/**
 * Le même chemin dans une autre langue — pour le sélecteur de langue. La
 * requête que la page possède voyage avec (`search`) : changer de langue sur
 * la cité ouverte sur le théâtre garde le théâtre.
 */
export function switchLang(pathname, lang, search = '') {
    const parsed = parseSitePath(pathname);
    if (!parsed.name) return sitePath('home', {}, lang);
    const path = sitePath(parsed.name, parsed.params, lang);
    return path ? path + pageQuery(parsed.name, search) : path;
}

// ?page=<slug> → page du site. Les slugs sont ceux de categories.js et des
// sous-pages du téléphone (MedardExperience.getPageFromUrl) ; les alias
// retirés (`itineraires`, `profil`) gardent leur atterrissage.
const PAGE_TO_SITE = Object.freeze({
    medard: 'medard',
    scan: 'medard',            // l'Œil vit sur le téléphone : la page Médard le dit, avec le QR
    monuments: 'monuments',
    'monuments-galerie': 'monuments',
    tresors: 'tresors',
    personnages: 'figures',
    'fetes-spectacles': 'fetes',
    balade: 'balades',
    itineraires: 'balades',
    manoir: 'manoir',
    passeport: 'manoir',
    profil: 'manoir',
    weekend: 'manoir',
    restaurants: 'manoir',
    demandes: 'manoir',
    'frise-voies': 'frise',
    'frise-couche': 'friseCouche',
    // Le lien que le téléphone sait ouvrir (MedardExperience → MenuSheet) :
    // envoyé à un ordinateur, il atterrit sur la page du site.
    [CITE_PAGE]: 'citeAntique',
});

const FICHE_TO_SITE = Object.freeze({
    monument: 'monument',
    personnage: 'figure',
    tresor: 'tresor',
    evenement: 'fete',
});

/**
 * Traduit une adresse de l'app (`search` d'une URL sur `/`) en chemin du site.
 * Retourne null quand rien dans la requête ne désigne une page — l'appelant
 * garde alors l'accueil. `fallbackLang` sert quand `?lang=` est absent
 * (la langue mémorisée de l'invité).
 */
export function legacyToSite(search, fallbackLang = SITE_DEFAULT_LANG) {
    let params;
    try {
        params = new URLSearchParams(
            typeof search === 'string' && search.startsWith('?') ? search.slice(1) : search || '',
        );
    } catch {
        return null;
    }
    const urlLang = params.get('lang');
    const lang = SITE_LANGS.includes(urlLang) ? urlLang : (SITE_LANGS.includes(fallbackLang) ? fallbackLang : SITE_DEFAULT_LANG);
    const page = params.get('page');
    // La cité en 3D AVANT les fiches : son `&monument=` ne désigne pas une
    // fiche à ouvrir, il dit vers quel monument la cité vole.
    if (page === CITE_PAGE) {
        return sitePath('citeAntique', {}, lang) + pageQuery('citeAntique', params);
    }
    for (const [kind, param] of Object.entries(FICHE_PARAMS)) {
        const id = params.get(param);
        if (id) return sitePath(FICHE_TO_SITE[kind], { id }, lang);
    }
    if (page && PAGE_TO_SITE[page]) {
        const name = PAGE_TO_SITE[page];
        if (name === 'friseCouche') {
            const couche = params.get('couche');
            if (couche && FRISE_COUCHES.includes(couche)) return sitePath('friseCouche', { couche }, lang);
            return sitePath('frise', {}, lang);
        }
        return sitePath(name, {}, lang);
    }
    if (urlLang && SITE_LANGS.includes(urlLang)) return sitePath('home', {}, lang);
    return null;
}

// page du site → ce que le téléphone sait ouvrir.
//
// ⚑ `medard: {}` n'est PAS un trou : `/` EST l'onglet Médard du téléphone
// (MobileShell, `tab-medard`). Et `balade` dégrade vers sa SECTION — le
// téléphone n'a pas d'adresse par balade, mais `?page=balade` ouvre l'étagère
// où elle se trouve : perdre la balade n'oblige pas à perdre la liste
// (audit du 10/09/2026, mobile n°2).
const SITE_TO_QUERY = Object.freeze({
    home: {},
    autun: { page: 'monuments' },
    monuments: { page: 'monuments' },
    tresors: { page: 'tresors' },
    figures: { page: 'personnages' },
    fetes: { page: 'fetes-spectacles' },
    frise: { page: 'frise-voies' },
    balades: { page: 'balade' },
    balade: { page: 'balade' },
    manoir: { page: 'manoir' },
    medard: {},
    livre: { display: 'book' },
    // La carte du site est le CANEVAS de l'onglet Explorer du téléphone ; il
    // n'existe pas de `?page=explorer` (MedardExperience.getPageFromUrl ne
    // le lit pas), mais `?page=monuments` ouvre cet onglet — où la carte est.
    carte: { page: 'monuments' },
    // La cité en 3D EXISTE sur le téléphone : le même visualiseur, en plein
    // écran. Elle n'est donc pas du seul grand écran (SITE_WIDE_ONLY) — le
    // téléphone l'ouvre par `?page=cite-antique`, lu par MedardExperience.
    citeAntique: { page: CITE_PAGE },
});

/**
 * Les pages du site qui n'ont AUCUNE adresse sur le téléphone — et qu'on ne
 * traduit donc pas. La Bibliothèque de l'app est un calque sans URL
 * (MenuSheet `setBiblioOpen`), un conte ne s'y ouvre pas par adresse, et les
 * suites n'ont pas de page dans l'onglet Manoir.
 *
 * ⚑ Jusqu'au 10/09/2026 ces trois-là rendaient `/` EN SILENCE : un lien de
 * conte partagé par messagerie, la page d'une suite envoyée à un client, un QR
 * de balade — tout ouvrait l'accueil de Médard, l'adresse effacée de la barre,
 * sans un mot. « Jamais une adresse inventée » était tenu ; « jamais muet »
 * ne l'était pas. `siteToLegacy` rend désormais null pour elles, et App.js
 * GARDE l'adresse et le dit (pages/WideScreenOnly.jsx). Les mentions légales
 * ne sont pas ici : elles se rendent à toute largeur (LCEN).
 */
export const SITE_WIDE_ONLY = Object.freeze(new Set(['bibliotheque', 'conte', 'suite']));

export const isWideOnly = (name) => SITE_WIDE_ONLY.has(name);

/**
 * Traduit une page du site en adresse de l'app : le téléphone ouvre la même
 * fiche, par le contrat qu'il connaît. Retourne `null` quand la page n'a
 * d'équivalent ni exact ni par section (SITE_WIDE_ONLY) — jamais une adresse
 * inventée, et jamais un `/` muet. Une page inconnue rend '/'. `search` est
 * la requête de l'adresse du site : seuls les paramètres que la page possède
 * (SITE_PAGE_QUERY) en sont repris.
 */
export function siteToLegacy({ name, params = {}, lang = SITE_DEFAULT_LANG, search = '' } = {}) {
    if (isWideOnly(name)) return null;
    const q = new URLSearchParams();
    if (name === 'monument' && params.id) q.set(FICHE_PARAMS.monument, params.id);
    else if (name === 'tresor' && params.id) q.set(FICHE_PARAMS.tresor, params.id);
    else if (name === 'figure' && params.id) q.set(FICHE_PARAMS.personnage, params.id);
    else if (name === 'fete' && params.id) q.set(FICHE_PARAMS.evenement, params.id);
    else if (name === 'friseCouche' && params.couche) {
        q.set('page', 'frise-couche');
        q.set('couche', params.couche);
    } else {
        const base = SITE_TO_QUERY[name] || {};
        Object.entries(base).forEach(([k, v]) => q.set(k, v));
    }
    // Ce que la page possède voyage tel quel : `/cite-antique?monument=x`
    // devient `?page=cite-antique&monument=x`, la cité du téléphone volant
    // vers le même monument.
    asParams(pageQuery(name, search)).forEach((v, k) => q.set(k, v));
    if (lang && lang !== SITE_DEFAULT_LANG && SITE_LANGS.includes(lang)) q.set('lang', lang);
    const s = q.toString();
    return s ? `/?${s}` : '/';
}
